import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useCartCtx } from '../App';
import { useCurrency } from '../hooks/useCurrency';
import { useRewardConfig } from '../hooks/useRewardConfig';
import api from '../utils/api';
import { toast } from 'react-toastify';

export default function ProductPage() {
  const { slug } = useParams();
  const { fmt } = useCurrency();
  const rewards = useRewardConfig();
  const [product, setProduct]                 = useState(null);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [quantity, setQuantity]               = useState(1);
  const [loading, setLoading]                 = useState(true);
  const { addItem } = useCartCtx();

  useEffect(() => {
    api.get('/products/' + slug)
      .then(res => { setProduct(res.data); setSelectedVariant(res.data.variants?.[0]); })
      .catch(() => toast.error('Product not found'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center text-tea-600">Loading...</div>
  );
  if (!product) return (
    <div className="min-h-screen flex items-center justify-center">Product not found.</div>
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />
      <main className="flex-1 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="bg-tea-50 rounded-2xl h-96 flex items-center justify-center overflow-hidden">
            {product.imageUrl ? (
              <img src={product.imageUrl} alt={product.name} className="w-full h-full object-cover rounded-2xl" />
            ) : (
              <svg viewBox="0 0 64 64" className="w-56 h-56 text-tea-600" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <rect x="22" y="6" width="20" height="10" rx="2" />
                <rect x="20" y="16" width="24" height="6" />
                <path d="M22 22h20l-2 34a4 4 0 0 1-4 4h-8a4 4 0 0 1-4-4z" />
                <path d="M26 30h12M26 40h12M26 50h12" strokeOpacity="0.4" />
              </svg>
            )}
          </div>

          <div>
            <h1 className="text-3xl font-bold mb-2">{product.name}</h1>
            <p className="text-gray-600 mb-6">{product.description}</p>

            <div className="mb-6">
              <p className="font-medium text-gray-700 mb-3">Choose option:</p>
              <div className="flex flex-wrap gap-3">
                {product.variants?.map(v => (
                  <button key={v.id} onClick={() => setSelectedVariant(v)}
                    className={'px-4 py-2 rounded-lg border-2 font-medium transition-colors ' +
                      (selectedVariant?.id === v.id
                        ? 'border-tea-600 bg-tea-50 text-tea-700'
                        : 'border-gray-200 hover:border-tea-300')}>
                    {v.name} — {fmt(v.priceInCents)}
                  </button>
                ))}
              </div>
            </div>

            {selectedVariant && (
              <div className="mb-6">
                <div className="text-3xl font-bold text-tea-700 mb-4">
                  {fmt(selectedVariant.priceInCents)}
                  {selectedVariant.comparePriceInCents > selectedVariant.priceInCents && (
                    <span className="text-xl text-gray-400 line-through ml-3">
                      {fmt(selectedVariant.comparePriceInCents)}
                    </span>
                  )}
                </div>

                <div className="flex items-center gap-4 mb-5">
                  <div className="flex items-center border border-gray-300 rounded-lg">
                    <button onClick={() => setQuantity(q => Math.max(1, q - 1))}
                      className="px-4 py-2 text-xl text-gray-600 hover:text-tea-700">-</button>
                    <span className="px-4 py-2 font-medium">{quantity}</span>
                    <button onClick={() => setQuantity(q => q + 1)}
                      className="px-4 py-2 text-xl text-gray-600 hover:text-tea-700">+</button>
                  </div>
                  <span className="text-gray-500 text-sm">{selectedVariant.inventory} in stock</span>
                </div>

                <button
                  onClick={() => { addItem(selectedVariant, product, quantity); toast.success('Added to cart!'); }}
                  disabled={selectedVariant.inventory < 1}
                  className="btn-primary w-full py-4 text-lg mb-5">
                  {selectedVariant.inventory < 1 ? 'Out of Stock' : 'Add to Cart'}
                </button>
              </div>
            )}

            <div className="flex flex-wrap gap-4 text-sm text-gray-600 mt-6">
              <span>Free shipping {fmt(rewards.freeShippingThresholdCents)}+</span>
              <span>Cruelty-free</span>
              <span>For external use only</span>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
